import type { EnvironmentConfig, FlightState, ShotResult as ShotResultType, SwingParams, Vec3 } from '../types';
import { SimPhase, SurfaceType } from '../types';
import { PHYSICS_DT, FLIGHT_SAMPLE_INTERVAL, DEFAULT_ENVIRONMENT, MPH_TO_MS, RAD_TO_DEG } from '../constants';
import { advanceShotFlight, createFlightSetup, flightEnvironment } from '../physics/FlightSetup';
import { GroundPhysics } from '../physics/GroundPhysics';
import { FlightRecorder } from './FlightRecorder';
import { computeShotResult, formatShotResult } from './ShotResult';
import { BallRenderer } from '../rendering/BallRenderer';
import { ShotTracer } from '../rendering/ShotTracer';
import { CameraSystem } from '../camera/CameraSystem';

export type ShotCompleteCallback = (result: ShotResultType, formatted: Record<string, string>) => void;

/**
 * Orchestrates a complete shot lifecycle:
 * 1. Flight (aerodynamic physics)
 * 2. Ground (Rapier bounce/roll)
 * 3. Rest (compute analytics)
 *
 * Camera stays behind tee for the entire shot (launch monitor style).
 */
export class ShotSimulator {
  private phase = SimPhase.Idle;
  private flightState: FlightState | null = null;
  private prevFlightState: FlightState | null = null;
  private groundPhysics = new GroundPhysics();
  private recorder = new FlightRecorder();
  private ball: BallRenderer;
  private tracer: ShotTracer;
  private camera: CameraSystem;
  private context = flightEnvironment(DEFAULT_ENVIRONMENT);
  private environment: EnvironmentConfig = { ...DEFAULT_ENVIRONMENT };
  private physicsFrameCount = 0;
  private groundTime = 0;
  private onComplete: ShotCompleteCallback | null = null;

  // Replay state
  private replayTime = 0;
  private replaySpeed = 0.25;
  private replayPlaying = false;
  private replayStatic = false; // true = camera stays put during replay

  constructor(ball: BallRenderer, tracer: ShotTracer, camera: CameraSystem) {
    this.ball = ball;
    this.tracer = tracer;
    this.camera = camera;
  }

  getPhase(): SimPhase {
    return this.phase;
  }

  setWind(wind: Vec3): void {
    this.setEnvironment({ windSpeed: Math.hypot(wind.x, wind.z) / MPH_TO_MS,
      windDirection: Math.atan2(wind.x, -wind.z) * RAD_TO_DEG });
  }

  /**
   * Configure environment settings (altitude, temperature, wind).
   * Recomputes air density and wind vector from the config.
   */
  setEnvironment(config: Partial<EnvironmentConfig>): void {
    this.environment = { ...this.environment, ...config };
    // Active shots retain the atmosphere captured at launch.
  }

  getEnvironment(): EnvironmentConfig {
    return { ...this.environment };
  }

  getAirDensity(): number {
    return flightEnvironment(this.environment).airDensity;
  }

  setOnComplete(cb: ShotCompleteCallback): void {
    this.onComplete = cb;
  }

  launch(params: SwingParams, cameraAngle?: 'behind' | 'side' | 'top'): void {
    this.groundPhysics.cleanup();
    this.recorder.clear();
    // Freeze previous shot tracer as dimmed history, start fresh
    this.tracer.freeze();

    const setup = createFlightSetup(params, this.environment);
    this.flightState = setup.state;
    this.context = setup.context;
    this.prevFlightState = { ...this.flightState };
    this.phase = SimPhase.Flight;
    this.physicsFrameCount = 0;
    this.groundTime = 0;

    this.recorder.record(this.flightState);

    // Set camera to selected angle for the shot
    this.camera.setShotMode(cameraAngle || 'behind');
  }

  physicsUpdate(dt: number): void {
    if (this.phase === SimPhase.Flight) {
      this.updateFlight(dt);
    } else if (this.phase === SimPhase.Ground) {
      this.updateGround(dt);
    } else if (this.phase === SimPhase.Replay) {
      this.updateReplay(dt);
    }
  }

  renderUpdate(alpha: number): void {
    if (this.phase === SimPhase.Flight && this.flightState && this.prevFlightState) {
      this.ball.setInterpolatedPosition(
        this.prevFlightState.position,
        this.flightState.position,
        alpha,
      );
      this.camera.update(PHYSICS_DT, this.flightState.position, this.flightState.velocity);

      // Rebuild tracer every render frame for perfectly smooth growth
      // Double-buffered mesh swap in ShotTracer prevents flicker
      this.tracer.buildFromFrames(this.recorder.getFrames());
    } else if (this.phase === SimPhase.Ground) {
      const pos = this.groundPhysics.getPosition();
      const vel = this.groundPhysics.getVelocity();
      this.ball.setPosition(pos);
      // Camera stays in shot mode — same stable view for ground phase
      this.camera.update(PHYSICS_DT, pos, vel);
    } else if (this.phase === SimPhase.Replay) {
      // Position is set in updateReplay
    }
  }

  private updateFlight(dt: number): void {
    if (!this.flightState) return;
    this.prevFlightState = this.flightState;
    const next = advanceShotFlight(this.flightState, dt, this.context);
    this.flightState = next.state;
    this.physicsFrameCount++;

    if (next.landed) {
      const landing = next.state;
      this.recorder.record(landing, 'landing');
      this.tracer.buildFromFrames(this.recorder.getFrames());
      this.phase = SimPhase.Ground;
      this.groundTime = landing.time;
      this.groundPhysics.startGroundPhase(landing.position, landing.velocity, SurfaceType.Fairway,
        landing.spinRate, landing.spinAxis);
    } else if (this.physicsFrameCount % FLIGHT_SAMPLE_INTERVAL === 0) {
      this.recorder.record(this.flightState);
    }
  }

  private updateGround(dt: number): void {
    this.groundTime += dt;
    const pos = this.groundPhysics.step(dt);
    const vel = this.groundPhysics.getVelocity();

    if (this.physicsFrameCount % (FLIGHT_SAMPLE_INTERVAL * 2) === 0) {
      this.recorder.recordGround(this.groundTime, pos, vel);
    }
    this.physicsFrameCount++;

    if (this.groundPhysics.atRest) {
      this.recorder.recordGround(this.groundTime, pos, { x: 0, y: 0, z: 0 });

      this.phase = SimPhase.Rest;
      this.ball.setPosition(pos);

      const result = computeShotResult(this.recorder.getFrames());
      const formatted = formatShotResult(result);
      this.onComplete?.(result, formatted);

      this.camera.setFreeLook();
    }
  }

  // ── Replay System ──

  startReplay(speed: number = 0.25): void {
    if (this.recorder.getFrameCount() < 2) return;

    this.phase = SimPhase.Replay;
    this.replayTime = 0;
    this.replaySpeed = speed;
    this.replayPlaying = true;
    this.replayStatic = false;
    this.tracer.dispose();
    this.camera.setReplay();
  }

  /**
   * Replay the shot without moving the camera — ball re-flies from
   * whatever angle the user has currently set up.
   */
  startStaticReplay(speed: number = 0.35): void {
    if (this.recorder.getFrameCount() < 2) return;

    this.phase = SimPhase.Replay;
    this.replayTime = 0;
    this.replaySpeed = speed;
    this.replayPlaying = true;
    this.replayStatic = true;
    this.tracer.dispose();
    // Don't touch the camera — user keeps their current view
  }

  setReplaySpeed(speed: number): void {
    this.replaySpeed = speed;
  }

  pauseReplay(): void {
    this.replayPlaying = false;
  }

  resumeReplay(): void {
    this.replayPlaying = true;
  }

  stopReplay(): void {
    this.phase = SimPhase.Rest;
    this.replayPlaying = false;
    this.replayStatic = false;
    this.camera.setFreeLook();
  }

  private updateReplay(dt: number): void {
    if (!this.replayPlaying) return;

    this.replayTime += dt * this.replaySpeed;
    const totalTime = this.recorder.getTotalTime();

    if (this.replayTime >= totalTime) {
      this.replayTime = totalTime;
      this.replayPlaying = false;
      this.phase = SimPhase.Rest;
      // Rebuild full tracer at end of replay
      this.tracer.buildFromFrames(this.recorder.getFrames());
      if (!this.replayStatic) this.camera.setFreeLook();
      this.replayStatic = false;
      return;
    }

    const frame = this.recorder.getFrameAtTime(this.replayTime);
    if (frame) {
      this.ball.setPosition(frame.position);
      // Only move camera during chase replay, not static replay
      if (!this.replayStatic) {
        this.camera.update(dt, frame.position, frame.velocity);
      }

      // Progressive tracer during replay
      const frames = this.recorder.getFrames();
      let upToIndex = 0;
      for (let i = 0; i < frames.length; i++) {
        if (frames[i].time <= this.replayTime) upToIndex = i;
        else break;
      }
      this.tracer.buildFromFrames(frames, upToIndex);
    }
  }

  getRecorder(): FlightRecorder {
    return this.recorder;
  }
}
